import React from "react";
import cn from "classnames";

type ColorInputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
};

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const normalizeHexColor = (value: string, fallback = "#000000") =>
  HEX_COLOR_RE.test(value) ? value.toUpperCase() : fallback;

const sanitizeHexDraft = (value: string) => {
  const cleaned = value.replace(/[^0-9a-fA-F#]/g, "").toUpperCase();
  if (!cleaned) return "";
  if (cleaned[0] === "#") return `#${cleaned.slice(1, 7)}`;
  return `#${cleaned.slice(0, 6)}`;
};

const ColorInput = ({
  label,
  value,
  onChange,
  className,
  disabled,
}: ColorInputProps) => {
  const [draft, setDraft] = React.useState(normalizeHexColor(value));

  React.useEffect(() => {
    setDraft(normalizeHexColor(value));
  }, [value]);

  const commitDraft = () => {
    onChange(normalizeHexColor(draft, normalizeHexColor(value)));
  };

  return (
    <div className={cn("flex flex-col gap-y-2 grow", className && className)}>
      <label className="text-gray-400 text-sm">{label}</label>
      <div
        className={cn(
          "flex items-center gap-2 border border-gray-300 px-2 py-1.5 min-w-0",
          disabled && "bg-vg-gray"
        )}
      >
        <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded border border-black/10">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: normalizeHexColor(value) }}
          />
          <input
            type="color"
            value={normalizeHexColor(value)}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            disabled={disabled}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(sanitizeHexDraft(event.target.value))}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
          }}
          placeholder="#000000"
          disabled={disabled}
          className="min-w-0 flex-1 bg-transparent font-mono text-sm uppercase outline-none"
        />
      </div>
    </div>
  );
};

export default ColorInput;
